// Local junk removal pages, shown at /junk-removal/<slug> and listed in the sitemap.
// City rules change: re-check the resource links once a year, and don't publish
// dates or fees that change every season.

export interface JunkRemovalCity {
  slug: string;
  name: string;
  // Page <title> (the site name is added automatically) and meta description
  title: string;
  description: string;
  tagline: string;
  heading: string;
  intro: string;
  communities: string[];
  sections: { heading: string; paragraphs: string[] }[];
  faqs: { question: string; answer: string }[];
  resources: { label: string; url: string }[];
}

const PRICE_ANSWER =
  'Junk removal starts at $100. The final price depends on how much space your items take up, how heavy they are and what it costs to dispose of or recycle them. Call (920) 255-8952 or send the form below for a free quote.';

export const junkRemovalCities: JunkRemovalCity[] = [
  {
    slug: 'green-bay',
    name: 'Green Bay',
    title: 'Junk Removal in Green Bay, WI',
    description:
      'Junk removal in Green Bay, De Pere, Ashwaubenon and nearby. We do the lifting, haul it away in our trailer, and donate or recycle what we can. From $100.',
    tagline: 'Local junk removal for Green Bay, De Pere, Ashwaubenon and nearby. We do the lifting and haul it away.',
    heading: 'Junk removal from a Green Bay crew',
    intro:
      "JBR Delivery is based right here in Green Bay. If you've got a garage, basement or spare room full of stuff you're done with, we'll come out, carry it out, load it into our trailer and haul it away. No waiting for bulk week, and no renting a truck.",
    communities: ['Green Bay', 'De Pere', 'Ashwaubenon', 'Allouez', 'Bellevue', 'Howard', 'Suamico', 'Hobart', 'Wrightstown', 'Pulaski'],
    sections: [
      {
        heading: 'Green Bay bulk pickup vs. hiring a hauler',
        paragraphs: [
          "The City of Green Bay picks up bulky items for free during two bulk weeks a year, one in spring and one in fall. There's a size limit, and the city won't take appliances, electronics or construction and remodeling debris. Outside those weeks, the city bills the property owner for bulky items left at the curb.",
          "Green Bay residents can also bring furniture and mattresses to the city's drop-off sites themselves. De Pere and the other suburbs run their own programs, so the rules depend on where you live.",
          "If your stuff doesn't fit the city's rules or timing, or you'd rather not drag it up the stairs and across town yourself, that's where we come in.",
        ],
      },
      {
        heading: 'Where your stuff goes',
        paragraphs: [
          'We donate and recycle what we can. Usable furniture can often go to local donation spots like the Habitat for Humanity ReStore or St. Vincent de Paul instead of the landfill.',
          "Wisconsin law keeps electronics and major appliances out of landfills, so those get recycled. Everything else is disposed of properly.",
        ],
      },
    ],
    faqs: [
      {
        question: 'Does the City of Green Bay pick up bulky items?',
        answer:
          "Yes, for free during its two bulk weeks each year, within a size limit and with some items excluded. Outside those weeks the city charges for bulky pickup. Check the city's website for this year's dates and rules, or call us to haul it whenever you're ready.",
      },
      {
        question: 'Do you serve De Pere, Ashwaubenon and Howard?',
        answer:
          "Yes. We cover Green Bay and the communities around it, including De Pere, Ashwaubenon, Allouez, Bellevue, Howard, Suamico and Hobart. If you're not sure we come to you, give us a call.",
      },
      { question: 'How much does junk removal cost in Green Bay?', answer: PRICE_ANSWER },
    ],
    resources: [
      { label: 'City of Green Bay: bulk pickup weeks', url: 'https://www.greenbaywi.gov/636/Overflow-Trash-Bulk-Pick-Up' },
      { label: 'City of Green Bay: bulk household items', url: 'https://www.greenbaywi.gov/640/Bulk-Household-Items' },
      { label: 'Brown County Waste Transfer Station', url: 'https://www.browncountyrecycling.org/waste-transfer-station' },
      { label: 'Brown County hazardous material drop-off', url: 'https://www.browncountyrecycling.org/hmr-hours-location' },
    ],
  },
  {
    slug: 'appleton',
    name: 'Appleton & the Fox Cities',
    title: 'Junk Removal in Appleton & the Fox Cities',
    description:
      'Junk removal in Appleton, Kaukauna, Kimberly, Little Chute, Neenah and Menasha. We do the lifting and haul it away, any time of year. From $100.',
    tagline: 'Local junk removal for Appleton, Kaukauna, Kimberly, Neenah and the rest of the Fox Cities.',
    heading: 'Junk removal in the Fox Cities',
    intro:
      "Appleton's free large-item pickup only runs part of the year and has a long list of things it won't take. We come out when you need us, carry everything out, load it into our trailer and haul it away.",
    communities: ['Appleton', 'Grand Chute', 'Kimberly', 'Little Chute', 'Kaukauna', 'Neenah', 'Menasha', 'Fox Crossing'],
    sections: [
      {
        heading: 'Appleton large-item pickup vs. hiring a hauler',
        paragraphs: [
          "The City of Appleton picks up a couple of large items at a time on garbage day, but only from April through September. It doesn't take appliances, mattresses, sleeper sofas, building materials, hot tubs, exercise equipment or pianos, and from October through March there's no large-item pickup at all.",
          "Outagamie County's Resource Recovery Park takes drop-offs by weight if you can get things there yourself. If you live in Neenah, Menasha or another Fox Cities community, check your own city's rules, since they're different from Appleton's.",
          "If the city won't take it, it's the wrong time of year, or you just want it gone without the heavy lifting, give us a call.",
        ],
      },
      {
        heading: 'Where your stuff goes',
        paragraphs: [
          'Usable furniture can often go to the Fox Cities Habitat for Humanity ReStore or St. Vincent de Paul instead of the landfill, and we donate what we can.',
          "Wisconsin law keeps electronics and major appliances out of landfills, so those get recycled. Everything else is disposed of properly.",
        ],
      },
    ],
    faqs: [
      {
        question: 'Does Appleton pick up large items in the winter?',
        answer: "No. Appleton's large-item pickup runs April through September. We can come out any time of year.",
      },
      {
        question: "Will you take things the city won't, like appliances or building materials?",
        answer:
          "Usually, yes. We haul appliances, furniture, and construction and remodeling debris. We can't take hazardous materials like chemicals or paint.",
      },
      { question: 'How much does junk removal cost in the Fox Cities?', answer: PRICE_ANSWER },
    ],
    resources: [
      {
        label: 'City of Appleton: garbage and large-item pickup',
        url: 'https://www.appletonwi.gov/government/departments/public_works/garbage_recycling/garbage.php',
      },
      { label: 'Outagamie County Resource Recovery Park', url: 'https://www.recyclemoreoutagamie.org/hourslocation/' },
      {
        label: 'Outagamie County household hazardous waste',
        url: 'https://www.recyclemoreoutagamie.org/household-hazardous-waste-disposal/',
      },
    ],
  },
  {
    slug: 'door-county',
    name: 'Door County',
    title: 'Junk Removal in Door County, WI',
    description:
      'Junk removal in Sturgeon Bay, Egg Harbor, Fish Creek, Sister Bay and across Door County. Cottage, garage and shed cleanouts. We do the lifting. From $100.',
    tagline: 'Junk removal for Door County homes, cottages and businesses, from Sturgeon Bay up the peninsula.',
    heading: 'Junk removal on the peninsula',
    intro:
      "Whether you live in Door County year-round or own a cottage you open and close each season, we'll clear out the old furniture, the garage or the shed and haul it all away.",
    communities: ['Sturgeon Bay', 'Egg Harbor', 'Fish Creek', 'Ephraim', 'Sister Bay', 'Baileys Harbor'],
    sections: [
      {
        heading: 'Getting rid of big stuff in Door County',
        paragraphs: [
          "Bulky-item pickup on the peninsula is limited. Sturgeon Bay collects large items once a month from spring through fall, by appointment and for a fee. Otherwise it usually means loading things up and driving them to a drop-off site yourself.",
          'Opening the cottage in spring or closing it in fall is a good time to clear out worn-out furniture, old appliances and whatever has piled up in the garage or shed.',
          'For paint, chemicals and other things we can\'t take, Door County holds household hazardous waste collection days.',
        ],
      },
      {
        heading: 'Where your stuff goes',
        paragraphs: [
          'Usable furniture can often go to the Door County Habitat for Humanity ReStore in Sturgeon Bay instead of the landfill, and we donate what we can.',
          "Wisconsin law keeps electronics and major appliances out of landfills, so those get recycled. Everything else is disposed of properly.",
        ],
      },
    ],
    faqs: [
      {
        question: 'Do you serve all of Door County?',
        answer:
          "We serve the Door County peninsula, including Sturgeon Bay, Egg Harbor, Fish Creek, Ephraim, Sister Bay and Baileys Harbor. If you're not sure we cover your spot, give us a call.",
      },
      {
        question: 'Can you clear out a cottage or vacation home?',
        answer:
          'Yes. We haul furniture, appliances, yard waste and garage or shed clutter for year-round residents, vacation homeowners and businesses.',
      },
      {
        question: 'Does it cost more up in Door County?',
        answer:
          'Junk removal starts at $100, and the price depends on how much there is and how far we drive. Call (920) 255-8952 or send the form below for a free quote.',
      },
    ],
    resources: [
      { label: 'City of Sturgeon Bay: public works and large-item pickup', url: 'https://www.sturgeonbaywi.org/departments/public_works/index.php' },
      { label: 'Door County Habitat for Humanity ReStore', url: 'https://www.doorhabitat.org/restore' },
    ],
  },
];

export const getJunkRemovalCity = (slug: string): JunkRemovalCity | undefined =>
  junkRemovalCities.find((city) => city.slug === slug);
